import type { MasterKeyState } from './types';

// API shapes for the gmail area: connected inboxes, the inbox guard's access log, the CRM address and the
// "Email & inboxes" admin section.

export type MailboxKind = 'personal' | 'shared' | 'crm';
export type MailboxStatus = 'connected' | 'error' | 'disconnected';

/**
 * What Clientbook did with an inbox, one row per batch in `mailbox_access_log`.
 * - check: new messages in a personal inbox compared by Gmail thread id only (nothing read)
 * - open: a personal-inbox message fetched because it answers a recent CRM conversation
 * - attachment: an attachment downloaded by a teammate, or the attachments of filed emails copied to file storage
 *   after the inbox's owner left the team
 * - sync: messages fetched from a shared inbox or the CRM address
 * - accept / reject: a message to the CRM address filed or turned away
 * - connect / disconnect: the inbox was connected or disconnected
 */
export const ACCESS_ACTIONS = ['check', 'open', 'attachment', 'sync', 'accept', 'reject', 'connect', 'disconnect'] as const;
export type AccessAction = (typeof ACCESS_ACTIONS)[number];

export interface MailboxInfo {
  id: string;
  kind: MailboxKind;
  address: string;
  displayName: string | null;
  /** Who connected it (the owner for personal inboxes). */
  userId: string | null;
  status: MailboxStatus;
  statusDetail: string | null;
  /** False for sample inboxes: shown for illustration only, Clientbook never contacts Google for them. */
  live: boolean;
  /** True when Calendar access was granted with the inbox (personal inboxes). */
  calendar: boolean;
  connectedAt: string | null;
  disconnectedAt: string | null;
  lastSyncAt: string | null;
  calendarSyncedAt: string | null;
  watchRenewedAt: string | null;
  watchExpiresAt: string | null;
  lastPushAt: string | null;
  /** Emails stored from this inbox. */
  emailCount: number;
  /** Disconnected, with Google access held while the attachments of its filed emails are copied to file storage. */
  keeping: boolean;
}

export interface AccessLogEntry {
  id: number;
  at: string;
  mailboxId: string;
  mailboxAddress: string;
  mailboxKind: MailboxKind;
  /** The person who connected the inbox. */
  mailboxUserId: string | null;
  action: AccessAction;
  count: number;
  detail: string | null;
  gmailMessageId: string | null;
  gmailThreadId: string | null;
  /** The teammate who caused it; null when Clientbook did it on its own. */
  actorId: string | null;
}

export interface AccessLogPage {
  entries: AccessLogEntry[];
  /** Pass back as `before` for the next page; null on the last page. */
  next: string | null;
}

export interface AccessToday {
  opened: number;
  checked: number;
}

/** GET /gmail/me: the account dialog's inbox section. */
export interface MyInbox {
  /** The super admin has saved a Google OAuth client. */
  configured: boolean;
  /** Google push notifications are set up (Pub/Sub topic saved). */
  push: boolean;
  /** Your most recent personal inbox, connected or not. */
  mailbox: MailboxInfo | null;
  replyWindowDays: number;
  /** CRM conversations in your inbox whose replies Clientbook currently opens. */
  guardedThreads: number;
  /** Upcoming meetings synced from your calendar. */
  upcomingMeetings: number;
  recent: AccessLogEntry[];
}

export interface SecretState {
  set: boolean;
  hint: string | null;
  updatedAt: string | null;
}

export interface PersonalInboxRow {
  userId: string;
  /** Null when this teammate has never connected Gmail. */
  mailbox: MailboxInfo | null;
  /** When the super admin last sent a reminder to connect. */
  nudgedAt: string | null;
}

export interface AcceptedAddress {
  email: string;
  userId: string;
  kind: 'work' | 'private';
}

/**
 * Why the CRM address turned a message away.
 * - unlisted: no active team member lists the sender's address
 * - unverified: Gmail could not confirm that the message really came from the sender's address
 */
export type RejectReason = 'unlisted' | 'unverified';

export interface RejectedForward {
  id: string;
  fromAddr: string;
  fromName: string | null;
  subject: string | null;
  receivedAt: string;
  reason: RejectReason;
  /** The teammate this address most likely belongs to, by name. */
  suggestedUserId: string | null;
}

/** GET /gmail/admin: the "Email & inboxes" admin section. */
export interface EmailAdmin {
  google: { configured: boolean; clientId: SecretState; clientSecret: SecretState };
  masterKey: MasterKeyState;
  /** The redirect URI to add to the Google OAuth client. */
  redirectUri: string;
  /** The site address Google must reach for push; push needs it to be public HTTPS. */
  baseUrl: string;
  publicHttps: boolean;
  pubsub: {
    topic: string | null;
    /** The push endpoint for the Pub/Sub subscription, secret token included. Null until a topic is saved. */
    pushUrl: string | null;
    /** The service account Google signs every push with; push is refused until it is saved. */
    pushAccount: string | null;
    active: boolean;
    lastPushAt: string | null;
    /** Why Google last refused to start push for an inbox (most often a missing publish permission on the topic). */
    watchError: { message: string; at: string } | null;
  };
  personal: PersonalInboxRow[];
  /** Personal inboxes of people who have left the team, connected or not. */
  formerPersonal: MailboxInfo[];
  shared: MailboxInfo[];
  crm: MailboxInfo | null;
  crmAddress: string | null;
  accepted: AcceptedAddress[];
  rejected: RejectedForward[];
  rejectedThisWeek: number;
  replyWindowDays: number;
  /** Your own personal inbox. */
  you: MailboxInfo | null;
  /** Your personal inbox can send a reminder email (it is connected to Google, not sample). */
  canSendNudge: boolean;
  waiting: number;
  errors: number;
}

/** GET /gmail/admin/attention: drives the dot on the admin nav item. */
export interface EmailAttention {
  waiting: number;
  errors: number;
}

/** A Google Cloud service account address, such as clientbook-push@my-project.iam.gserviceaccount.com. */
export const PUSH_ACCOUNT_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}@[a-z0-9][a-z0-9.-]*\.gserviceaccount\.com$/;

export const PUBSUB_TOPIC_PATTERN = /^projects\/[a-z][a-z0-9-]{4,28}[a-z0-9]\/topics\/[A-Za-z][A-Za-z0-9._~%+-]{2,254}$/;
